import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  InitiatePaymentRequestBody,
  PurchasePlan,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import { handleInitiatePayment } from "../api-clients/walletsPaymentsClient";
import { InitiatePaymentStates } from "../components/Payments/types";
import {
  TopupOrigins,
  SPECIAL_OFFER_MARKER,
  BONUS_GEM_MARKER,
  BONUS_HPR_MARKER,
} from "../utils/constants";
import { setStoredTopupOrigin } from "../utils/storage";
import useEffectAfterMount from "./useEffectAfterMount";
import usePayments, {
  generatePaymentParams,
  MAX_PAYMENT_RETRIES,
} from "./usePayments";
import { topupInfoAtom } from "../jotai/atoms/topup";
import useAnalytics from "./useAnalytics";
import { initiateTopupTriggerAtom } from "../jotai/atoms/analytics";
import { MetadataObj } from "./usePurchasePlans";
import LoadingOverlay, {
  OverlayVariant,
} from "../components/LoadingOverlay/LoadingOverlay";
import { ThemeVariant } from "../utils/constants/generic-types";

interface UseInitiateTopupProps {
  purchasePlans?: PurchasePlan[];
  plansMetadataObj: MetadataObj;
}

export default function useInitiateTopup({
  purchasePlans,
  plansMetadataObj,
}: UseInitiateTopupProps) {
  const { sendInitiateTopupEvent, sendContinueTopupEvent } = useAnalytics();
  const [loading, setLoading] = useState<boolean>(false);
  const [initiatingPaymentState, setInitiatingPaymentState] =
    useState<InitiatePaymentStates>(InitiatePaymentStates.INITIATING);
  const {
    paymentRetries,
    updatePaymentRetries,
    getPaymentRetryCount,
    generatePaymentRetryData,
    deletePaymentRetryData,
  } = usePayments();
  const setTopupInfo = useSetAtom(topupInfoAtom);
  const [selectedPurchasePlanId, setSelectedPurchasePlanId] =
    useState<PurchasePlan["id"]>("");
  const [selectedPurchasePlanIsSpecial, setSelectedPurchasePlanIsSpecial] =
    useState<boolean>(false);
  const selectedPurchasePlan = useMemo(() => {
    return purchasePlans?.find((plan) => plan.id === selectedPurchasePlanId);
  }, [purchasePlans, selectedPurchasePlanId]);
  const [initiateTopupTrigger, setInitiateTopupTrigger] = useAtom(
    initiateTopupTriggerAtom
  );

  const generatePaymentLink = useCallback(
    async (
      paymentData: InitiatePaymentRequestBody,
      paymentRetryCount: number,
      paymentMethod?: string
    ) => {
      const { planId } = paymentData;
      const requestBody = { ...paymentData, isRetry: !!paymentRetryCount };
      const paymentResponse = await handleInitiatePayment(requestBody);
      if (!paymentResponse) {
        setInitiatingPaymentState(InitiatePaymentStates.INITIATING_FAILED);
        updatePaymentRetries({
          ...paymentRetries,
          [planId]: generatePaymentRetryData(paymentRetryCount + 1),
        });
        return;
      }

      deletePaymentRetryData(planId);
      setTopupInfo((prevInfo) => ({
        ...prevInfo,
        price: selectedPurchasePlan?.price,
        isSpecialOffer: paymentData.isSpecialOffer,
      }));
      const paymentUrl = paymentMethod
        ? `${paymentResponse.url}&method=${paymentMethod}`
        : paymentResponse.url;
      window.location.href = paymentUrl;
    },
    [
      paymentRetries,
      selectedPurchasePlan?.price,
      setTopupInfo,
      updatePaymentRetries,
      generatePaymentRetryData,
      deletePaymentRetryData,
    ]
  );

  const initPayment = (paymentProvider?: string, paymentMethod?: string) => {
    setLoading(true);
    setStoredTopupOrigin(TopupOrigins.WEB);
    const url = new URL(selectedPurchasePlan?.paymentLink || "");
    const paymentParams = generatePaymentParams(url.searchParams);
    paymentParams.provider = paymentProvider ?? paymentParams.provider;
    const paramValues = Object.values(paymentParams);
    const isSpecialOffer = selectedPurchasePlanIsSpecial;

    if (paymentParams.provider && selectedPurchasePlan) {
      const { amount, price, id } = selectedPurchasePlan;
      const metadata = plansMetadataObj[id] ?? {};
      const specialOffer = metadata[SPECIAL_OFFER_MARKER] ?? 0;
      const gem = isSpecialOffer ? 0 : metadata[BONUS_GEM_MARKER] ?? 0;
      const bonusHpr = isSpecialOffer
        ? specialOffer - amount
        : metadata[BONUS_HPR_MARKER] ?? 0;

      sendContinueTopupEvent({
        payment_option: paymentParams.provider.toLowerCase(),
        hpr_amount: amount,
        fiat_amount: price,
        bonus_hpr_amount: bonusHpr,
        gem_amount: gem,
      });
    }

    if (paramValues.includes("") || paramValues.includes(null)) {
      // Show the payment initiation loader before the INVALID_URL state is shown
      setTimeout(
        () => setInitiatingPaymentState(InitiatePaymentStates.INVALID_URL),
        1500
      );
      setTimeout(() => setLoading(false), 3500);
      return;
    }

    const planId = paymentParams.planId as string;
    const paymentRetryCount = getPaymentRetryCount(planId);
    if (paymentRetryCount >= MAX_PAYMENT_RETRIES) {
      setInitiatingPaymentState(InitiatePaymentStates.INITIATING_FAILED);
      deletePaymentRetryData(planId);
      return;
    }

    const paymentData = {
      ...paymentParams,
      isSpecialOffer,
    } as InitiatePaymentRequestBody;
    generatePaymentLink(paymentData, paymentRetryCount, paymentMethod)
      .catch()
      .finally(() => setTimeout(() => setLoading(false), 2000));
  };

  useEffectAfterMount(() => {
    if (!loading) {
      setInitiatingPaymentState(InitiatePaymentStates.INITIATING);
    }
  }, [loading]);

  useEffect(() => {
    if (!initiateTopupTrigger) return;
    sendInitiateTopupEvent(initiateTopupTrigger);
    setInitiateTopupTrigger(undefined);
  }, [initiateTopupTrigger, sendInitiateTopupEvent, setInitiateTopupTrigger]);

  return {
    loading,
    initiatingPaymentState,
    initPayment,
    selectedPurchasePlanId,
    setSelectedPurchasePlanId,
    selectedPurchasePlan,
    selectedPurchasePlanIsSpecial,
    setSelectedPurchasePlanIsSpecial,
  };
}

export function InitiatingPaymentLoader({
  state,
  theme,
}: {
  state: InitiatePaymentStates;
  theme?: ThemeVariant;
}) {
  const { t } = useTranslation("wallet");
  const initiatePaymentStateMessages = useMemo(
    () => ({
      [InitiatePaymentStates.INITIATING]: `${t("initiating_payment")}...`,
      [InitiatePaymentStates.INVALID_URL]: t("payment_initiation_failed"),
      [InitiatePaymentStates.INITIATING_FAILED]: t("payment_initiation_failed"),
    }),
    [t]
  );

  return (
    <LoadingOverlay
      variant={OverlayVariant.SPINNER}
      loadingText={initiatePaymentStateMessages[state]}
      theme={theme}
    />
  );
}
